import _ from 'lodash'
import { createRef } from '@wix/materializer'
import type { ObsoleteBehaviorsList, ActionBehavior } from '@wix/thunderbolt-becky-types'
import type { Ref } from '@wix/thunderbolt-types'
import { COMP_ID_TO_INFLATED_COMP_ID_ASPECT, RESOLVED_REFS_NAMESPACE } from '../../constants'
import { createTemplateDataNode } from './utils/dataNodeUtils'
import type { DataNodeData, DataNodeDependencies } from './types'

type Dependencies = DataNodeDependencies | undefined

export const getItemsList = (itemsString: string): Array<ActionBehavior> | null => {
	try {
		const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
		return itemsList
	} catch (e) {
		console.error('Error parsing ObsoleteBehaviorsList', e)
	}

	return null
}

const createRefToInflatedId = (id: string): Ref<string> =>
	createRef([RESOLVED_REFS_NAMESPACE, COMP_ID_TO_INFLATED_COMP_ID_ASPECT, id])

export const Behaviors = createTemplateDataNode<ObsoleteBehaviorsList, Dependencies, DataNodeData<Dependencies>>({
	getDependencies: (item) => {
		if (!item) {
			return
		}

		// this way we can make sure sourceId and targetId are resolved first
		const itemsList = getItemsList(item.items)
		return _.reduce(
			itemsList,
			(deps, actionBehaviorItem) => {
				const sourceId = _.get(actionBehaviorItem, ['action', 'sourceId'])
				const targetId = _.get(actionBehaviorItem, ['behavior', 'targetId'])

				if (sourceId) {
					deps[sourceId] = createRefToInflatedId(sourceId)
				}

				if (targetId) {
					deps[targetId] = createRefToInflatedId(targetId)
				}

				return deps
			},
			{} as DataNodeDependencies
		)
	},
	toViewItem: ({ rawItem, value }) => {
		if (!rawItem) {
			return
		}

		const itemsList = getItemsList((rawItem as ObsoleteBehaviorsList).items)
		const { custom: deps } = value
		const fixedItems = _.map(itemsList, (actionBehaviorItem) => {
			const actionSourceId = _.get(actionBehaviorItem, ['action', 'sourceId'])
			const behaviorTargetId = _.get(actionBehaviorItem, ['behavior', 'targetId'])

			if (actionSourceId) {
				actionBehaviorItem.action.sourceId = deps?.[actionSourceId] || actionSourceId
			}

			if (behaviorTargetId) {
				actionBehaviorItem.behavior.targetId = deps?.[behaviorTargetId] || behaviorTargetId
			}

			return actionBehaviorItem
		})

		return {
			...rawItem,
			items: JSON.stringify(fixedItems),
		}
	},
})
